import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider/AuthProvider";
import { Box, CircularProgress, Typography, Alert, Button } from "@mui/material";
import { Logout } from "@mui/icons-material";

export const OtherUserLogin: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { otherUserLogin, isExternalLogin, logout } = useAuth();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const processLogin = async () => {
      try {
        // Parse URL parameters
        const searchParams = new URLSearchParams(location.search);

        // Get the complete data object
        const dataParam = searchParams.get("data");

        if (!dataParam) {
          setError("Missing required data parameter");
          setLoading(false);
          return;
        }

        let externalUserData;
        try {
          externalUserData = JSON.parse(decodeURIComponent(dataParam));
        } catch (e) {
          console.error("Error parsing user data:", e);
          setError("Invalid data format");
          setLoading(false);
          return;
        }

        const { token, user } = externalUserData;

        if (!token || !user) {
          setError("Missing required parameters in data");
          setLoading(false);
          return;
        }

        setLoading(true);

        // Pass complete user data to otherUserLogin
        otherUserLogin(
          externalUserData, // Pass the complete user object
          (isSuccess, error) => {
            setLoading(false);
            if (!isSuccess && error) {
              setError(error);
            } else {
              window.location.href = "/dashboard";
            }
          },
        );
      } catch (err) {
        console.error("Error in other user login:", err);
        setError("Failed to process login");
        setLoading(false);
      }
    };

    processLogin();
  }, [location.search, otherUserLogin, navigate]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
      >
        <CircularProgress />
        <Typography ml={2} mt={2}>Logging you in as external user...</Typography>
        {isExternalLogin && (
          <Alert severity="info" sx={{ mt: 2, maxWidth: 600 }}>
            Your original session is preserved and will be restored when you log out.
          </Alert>
        )}
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
      >
        <Typography color="error" variant="h6">Error: {error}</Typography>
        <Button
          variant="contained"
          startIcon={<Logout />}
          onClick={logout}
          sx={{ mt: 2 }}
        >
          Return to Original Session
        </Button>
      </Box>
    );
  }

  return null;
};
